import { unByKey } from 'ol/Observable';
import Overlay from 'ol/Overlay';
import { getArea, getLength } from 'ol/sphere';
import useOlEvent from '@/hooks/useOlEvent';
import { measureDrawArea, measureDrawLength } from '@/ol/interactions';
import { getFormattedArea, getFormattedLength } from '@/utils/stringutils';

/**
 * Custom hook to handle measure draw events.
 * @param {Object} measureDraw - The measure draw interaction.
 * @param {Object} eventHandlers - The event handlers for draw change, start, and end.
 */
function useMeasure(measureDraw, eventHandlers) {
  const { onDrawChange, onDrawStart, onDrawEnd } = eventHandlers;

  useOlEvent(measureDraw, 'change:active', onDrawChange);
  useOlEvent(measureDraw, 'drawstart', onDrawStart);
  useOlEvent(measureDraw, 'drawend', onDrawEnd);
}

/**
 * Custom hook to handle measure interactions.
 * @param {Object} map - The OpenLayers Map instance.
 */
export default function useMeasures(map) {
  let sketch, measureListener, measureTooltipElement, measureTooltip, tooltipCoord;

  /**
   * Function to create a measure tooltip.
   */
  const createMeasureTooltip = () => {
    if (measureTooltipElement) {
      measureTooltipElement.parentNode.removeChild(measureTooltipElement);
    }
    measureTooltipElement = document.createElement('div');
    measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure';
    measureTooltip = new Overlay({
      element: measureTooltipElement,
      offset: [0, -15],
      stopEvent: false,
      positioning: 'bottom-center',
    });

    if (map) {
      map.addOverlay(measureTooltip);
    }
  };

  /**
   * Event handler for draw change event.
   * @param {Object} event - The draw change event.
   */
  const onDrawChange = ({ oldValue }) => {
    if (oldValue === false) {
      createMeasureTooltip();
    }
  };

  /**
   * Event handler for draw start event.
   * @param {Object} drawEvent - The draw start event.
   */
  const onDrawStart = (drawEvent) => {
    sketch = drawEvent.feature;
    tooltipCoord = drawEvent.coordinate;

    let measure, formattedMeasure;

    measureListener = sketch.getGeometry().on('change', ({ target }) => {
      const geomType = target.getType();

      switch (geomType) {
        case 'LineString':
          measure = getLength(target);
          formattedMeasure = getFormattedLength(measure);
          tooltipCoord = target.getLastCoordinate();
          break;
        case 'Polygon':
          measure = getArea(target);
          formattedMeasure = getFormattedArea(measure);
          tooltipCoord = target.getInteriorPoint().getCoordinates();
          break;
        default:
          break;
      }

      measureTooltipElement.innerHTML = formattedMeasure;
      measureTooltip.setPosition(tooltipCoord);
    });
  };

  /**
   * Event handler for draw end event.
   */
  const onDrawEnd = () => {
    measureTooltipElement.className = 'ol-tooltip ol-tooltip-static';
    measureTooltip.setOffset([0, -7]);

    sketch = null;
    measureTooltipElement = null;

    createMeasureTooltip();
    unByKey(measureListener);
  };

  const measureOpts = {
    onDrawChange,
    onDrawStart,
    onDrawEnd,
  };

  measureDrawArea.setActive(false);
  measureDrawLength.setActive(false);

  useMeasure(measureDrawArea, measureOpts);
  useMeasure(measureDrawLength, measureOpts);
}
